import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Search, Stethoscope, Pill, BookOpen, GraduationCap, Loader2, AlertCircle } from "lucide-react";
import { NurseLayout } from "@/components/layout/nurse-layout";
import { PatientLayout } from "@/components/layout/patient-layout";
import { DoctorLayout } from "@/components/layout/doctor-layout";
import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { PhysioLayout } from "@/components/layout/physio-layout";
import { OTLayout } from "@/components/layout/ot-layout";

type SearchResultType = "condition" | "medication" | "guideline" | "scenario";

interface SearchResultItem {
  type: SearchResultType;
  id: number;
  title: string;
  subtitle: string | null;
}

const TYPE_CONFIG: Record<SearchResultType, { label: string; icon: React.ElementType; badge: string; cardBorder: string }> = {
  condition: {
    label: "Condition",
    icon: Stethoscope,
    badge: "bg-teal-50 text-teal-700 border-teal-100",
    cardBorder: "border-l-teal-400",
  },
  medication: {
    label: "Medication",
    icon: Pill,
    badge: "bg-blue-50 text-blue-700 border-blue-100",
    cardBorder: "border-l-blue-400",
  },
  guideline: {
    label: "Guideline",
    icon: BookOpen,
    badge: "bg-purple-50 text-purple-700 border-purple-100",
    cardBorder: "border-l-purple-400",
  },
  scenario: {
    label: "Scenario",
    icon: GraduationCap,
    badge: "bg-amber-50 text-amber-700 border-amber-100",
    cardBorder: "border-l-amber-400",
  },
};

const TYPE_FILTERS: { value: SearchResultType | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "condition", label: "Conditions" },
  { value: "medication", label: "Medications" },
  { value: "guideline", label: "Guidelines" },
  { value: "scenario", label: "Scenarios" },
];

function getDestinationUrl(item: SearchResultItem, role: string): string {
  if (role === "patient" || role === "guest") {
    if (item.type === "condition") return `/patient/conditions/${item.id}`;
    if (item.type === "medication") return `/patient/know-your-medication/${item.id}`;
  }
  if (item.type === "condition") return `/nurse/conditions/${item.id}`;
  if (item.type === "medication") return `/nurse/pharmacology/${item.id}`;
  if (item.type === "guideline") return `/nurse/guidelines/${item.id}`;
  if (item.type === "scenario") return `/nurse/scenarios/${item.id}`;
  return "/";
}

function SearchContent() {
  const [location, setLocation] = useLocation();
  const params = new URLSearchParams(location.includes("?") ? location.slice(location.indexOf("?")) : "");
  const initialQ = params.get("q") ?? "";

  const [inputValue, setInputValue] = useState(initialQ);
  const [query, setQuery] = useState(initialQ);
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<SearchResultType | "all">("all");

  const role = localStorage.getItem("rheumhub_role") ?? "nurse";
  const isProfessional = role !== "patient" && role !== "guest";

  const visibleFilters = isProfessional
    ? TYPE_FILTERS
    : TYPE_FILTERS.filter((f) => f.value !== "guideline" && f.value !== "scenario");

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setHasSearched(false);

    fetch(`/api/search?q=${encodeURIComponent(query.trim())}`)
      .then((r) => {
        if (!r.ok) throw new Error("Search failed");
        return r.json() as Promise<SearchResultItem[]>;
      })
      .then((data) => {
        setResults(data);
        setHasSearched(true);
      })
      .catch(() => {
        setError("Search failed. Please try again.");
        setResults([]);
      })
      .finally(() => setIsLoading(false));
  }, [query]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = inputValue.trim();
    if (!q) return;
    setQuery(q);
    setActiveFilter("all");
    setLocation(`/search?q=${encodeURIComponent(q)}`);
  };

  const filteredResults =
    activeFilter === "all" ? results : results.filter((r) => r.type === activeFilter);

  const countsByType = results.reduce<Record<string, number>>((acc, r) => {
    acc[r.type] = (acc[r.type] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="search-page">
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Search conditions, medications, guidelines, scenarios…"
            className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 bg-white"
            data-testid="search-page-input"
          />
        </div>
        <button
          type="submit"
          className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
          data-testid="search-page-submit"
        >
          Search
        </button>
      </form>

      {query.trim().length >= 2 && (
        <div className="flex items-center gap-2 flex-wrap">
          {visibleFilters.map((f) => {
            const count = f.value === "all" ? results.length : (countsByType[f.value] ?? 0);
            return (
              <button
                key={f.value}
                onClick={() => setActiveFilter(f.value)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  activeFilter === f.value
                    ? "bg-teal-600 text-white border-teal-600"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                {f.label}
                <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                  activeFilter === f.value ? "bg-teal-500 text-white" : "bg-slate-100 text-slate-500"
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16 gap-3 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Searching…</span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-lg text-red-700">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {!isLoading && hasSearched && filteredResults.length === 0 && (
        <div className="text-center py-16 space-y-2">
          <Search className="h-10 w-10 text-slate-300 mx-auto" />
          <p className="text-slate-600 font-medium">No results for "{query}"</p>
          <p className="text-sm text-slate-400">Try a different spelling or a broader term</p>
        </div>
      )}

      {!isLoading && filteredResults.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-slate-500">
            {filteredResults.length} result{filteredResults.length !== 1 ? "s" : ""}{" "}
            {activeFilter !== "all" ? `for ${TYPE_CONFIG[activeFilter].label.toLowerCase()}s` : ""}
            {query ? ` matching "${query}"` : ""}
          </p>
          <ul className="space-y-2">
            {filteredResults.map((item) => {
              const cfg = TYPE_CONFIG[item.type];
              const Icon = cfg.icon;
              const href = getDestinationUrl(item, role);
              return (
                <li key={`${item.type}-${item.id}`}>
                  <a
                    href={href}
                    onClick={(e) => {
                      e.preventDefault();
                      setLocation(href);
                    }}
                    className={`flex items-center gap-4 p-4 bg-white border border-slate-200 border-l-4 ${cfg.cardBorder} rounded-lg hover:shadow-sm transition-shadow group`}
                    data-testid={`search-result-${item.type}-${item.id}`}
                  >
                    <div className={`shrink-0 p-2 rounded-lg border ${cfg.badge}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 group-hover:text-teal-700 transition-colors truncate">
                        {item.title}
                      </p>
                      {item.subtitle && (
                        <p className="text-sm text-slate-500 mt-0.5 truncate">{item.subtitle}</p>
                      )}
                    </div>
                    <span className={`shrink-0 text-xs font-medium px-2 py-1 rounded border ${cfg.badge}`}>
                      {cfg.label}
                    </span>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!query && !isLoading && (
        <div className="text-center py-16 space-y-2">
          <Search className="h-10 w-10 text-slate-300 mx-auto" />
          <p className="text-slate-500">Enter a search term to find conditions, medications, guidelines, and scenarios</p>
        </div>
      )}
    </div>
  );
}

type LayoutComponent = React.ComponentType<{ children: React.ReactNode; title: string }>;

const ROLE_LAYOUT: Record<string, LayoutComponent> = {
  nurse: NurseLayout,
  doctor: DoctorLayout,
  pharmacist: PharmacistLayout,
  physio: PhysioLayout,
  ot: OTLayout,
  patient: PatientLayout,
  guest: PatientLayout,
};

export default function SearchPage() {
  const role = localStorage.getItem("rheumhub_role") ?? "nurse";
  const Layout = ROLE_LAYOUT[role] ?? NurseLayout;

  return (
    <Layout title="Search">
      <SearchContent />
    </Layout>
  );
}
